import { useQuery } from "@apollo/client/react";
import {
  DSButton,
  DSInput,
} from "@components/design-system/molecules";
import { OpenPipButton } from "@components/pip/OpenPipButton.jsx";
import {
  faExternalLinkAlt,
  faFilter,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_INBOX_NOTES_LISTING from "@graphql/queries/GetInboxNotesListing";
import { GET_PENDING_USER_TEAM_INVITES } from "@graphql/queries/GetUserTeamInvites";
import { Tooltip } from "@mui/material";
import {
  NOTE_SEARCH_PLACEHOLDER,
  SEARCH_DEBOUNCE_TIME,
} from "@src/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import NoteList from "./NoteList";
import TeamInvite from "./TeamInvite";
import {
  useDebouncedValue,
  useLocalStorage,
  usePreventRecordingInterruptions,
  useWatchUserWsEvent,
} from "../../../hooks";
import { useRecordingJobType } from "../../../hooks";
import { useTeam } from "../../../hooks/use-team";
import Sort from "../../common/inbox/Sort";
import NoteFilters from "../../inbox/NoteFilters";

export function DashboardInbox() {
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortAscending, setSortAscending] = useState(false);
  const { inboxSearch } = useFlags();

  const { control, register } = useForm({
    defaultValues: {
      searchQuery: "",
    },
  });

  const navigate = useNavigate();
  const { handleActionWithPrevention } =
    usePreventRecordingInterruptions();
  const {
    currentTeam,
    selectedTeamMembers,
    setSelectedTeamMembers,
    refreshUserTeams,
  } = useTeam();

  const [selectedRange, setSelectedRange] = useLocalStorage(
    "selected-date-range",
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  );

  const { filterJobType, setFilterJobType } = useRecordingJobType();
  const [queryState, setQueryState] = useState({
    filter: "unapproved",
    limit: 25,
    offset: 0,
  });
  const searchQuery = useWatch({ control, name: "searchQuery" });
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    SEARCH_DEBOUNCE_TIME,
  );
  const queryOptions = {
    ...queryState,
    teamUuid: currentTeam?.uuid,
    userUuids: selectedTeamMembers,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    sortAscending,
    jobType: filterJobType?.value,
    searchQuery: debouncedSearchQuery,
  };

  const { data, refetch, loading, fetchMore } = useQuery(
    GET_INBOX_NOTES_LISTING,
    {
      variables: queryOptions,
      fetchPolicy: "network-only",
    },
  );
  useWatchUserWsEvent({
    onMessage: () => refetch(queryOptions),
    eventName: "note_update",
  });

  const { data: teamInviteData, refetch: refetchTeamInvites } =
    useQuery(GET_PENDING_USER_TEAM_INVITES, {
      fetchPolicy: "network-only",
    });

  useWatchUserWsEvent({
    onMessage: refetchTeamInvites,
    eventName: "team_invite",
  });

  function incrementOffset() {
    setLoadingMore(true);

    fetchMore({
      variables: {
        offset: data?.inboxNotes?.notes?.length || 0,
      },
    }).then(() => {
      setLoadingMore(false);
    });
  }

  const navigateToInbox = () => {
    navigate("/inbox");
  };

  const handleSort = () => {
    setSortAscending((prev) => !prev);
  };

  return (
    <div className="relative border border-primary bg-primary rounded-xl shadow-xs flex flex-col">
      <div className="flex flex-col w-full rounded-xl bg-secondary border-b border-secondary p-4 shadow-xs">
        <div className="flex flex-row items-center justify-between flex-1">
          <h3 className="text-primary">
            Inbox{" "}
            <Tooltip
              arrow
              placement="right"
              title="This is the number of notes in your current Inbox view (according to any filters applied)."
            >
              <span
                className="text-secondary text-base"
                data-testid="inbox-note-count"
              >
                {data?.inboxNotes?.totalNotes}
              </span>
            </Tooltip>
          </h3>

          <div className="flex flex-row flex-wrap gap-2 justify-end">
            <OpenPipButton pipUrl="/pip/notes" />

            <DSButton
              variant="primary"
              size="sm"
              icon={faExternalLinkAlt}
              onClick={() => {
                handleActionWithPrevention(navigateToInbox);
              }}
              dataTestId="expand-inbox-button"
            >
              Expand
            </DSButton>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-2 border-t border-secondary pt-2">
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center space-x-1">
              <FontAwesomeIcon
                icon={faFilter}
                className="text-secondary"
              />
              <NoteFilters
                searchQuery={debouncedSearchQuery}
                setQueryOptions={setQueryState}
                currentTeam={currentTeam}
                selectedTeamMembers={selectedTeamMembers}
                setSelectedTeamMembers={setSelectedTeamMembers}
                selectedJobType={filterJobType}
                setSelectedJobType={setFilterJobType}
                refreshCurrentTeam={refreshUserTeams}
                noteFilter="unapproved"
                selectedRange={selectedRange}
                setSelectedRange={setSelectedRange}
              />
            </div>
            <div className="flex flex-row items-center space-x-1">
              <Sort
                sortAscending={sortAscending}
                onSort={handleSort}
              />
            </div>
          </div>
          {inboxSearch && (
            <DSInput
              leadingIcon={faSearch}
              placeholder={NOTE_SEARCH_PLACEHOLDER}
              dataTestId="dashboard-inbox-search"
              {...register("searchQuery")}
            />
          )}
        </div>
      </div>

      <div className="flex flex-col items-start rounded-b-lg pt-2 px-2 w-full">
        {teamInviteData?.pendingUserTeamInvites?.length > 0 && (
          <div className="w-full px-[2%] flex flex-col gap-2">
            {teamInviteData?.pendingUserTeamInvites.map((invite) => (
              <TeamInvite key={invite.uuid} invite={invite} />
            ))}
          </div>
        )}
        <div className="relative flex flex-row items-center justify-end w-full">
          <div className="z-10 bg-linear-to-b from-primary via-primary absolute top-0 w-full flex justify-center pt-5 px-5"></div>
        </div>

        <div className="min-h-[400px] relative w-full">
          <NoteList
            totalNotes={data?.inboxNotes?.totalNotes || 0}
            items={data?.inboxNotes?.notes || []}
            refetch={() => refetch(queryOptions)}
            incrementOffset={incrementOffset}
            loadingMore={loadingMore}
            loading={loading}
            handleActionWithPrevention={handleActionWithPrevention}
            dataTestId="dashboard-note-list"
          />
        </div>
      </div>
    </div>
  );
}
